/**
 * M2 — "Mindset" — THE BRANCH POINT.
 *
 * Verbatim from the original prototype `QUIZ` (app.js L141-162), terse slider
 * labels `QUIZ_LABELS` (app.js L1003-1008), and full anchor prose
 * `CONTENT.m2.questions` (content.js L161-194).
 *
 * Mechanic: 4 questions, each a 0–6 range slider (7 stops). Slider value maps to
 * an answer via `sliderToAnswer`:
 *   value 0,1,2 → 'A'   (corporate / corp lean — this is the scored option)
 *   value 3     → 'gen' (middle / split — counts as NEITHER A nor B)
 *   value 4,5,6 → 'B'   (investing lean)
 *
 * `deriveArchetype` (see use-finance-discovery-store) counts the 'A' answers:
 * ≥3 → corp, ≤1 → invest, exactly 2 → gen.
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md §3.
 */

/** A single M2 answer once a slider settles. 'A' = corp-lean (the scored option). */
export type M2Answer = 'A' | 'B' | 'gen';

export interface M2Question {
  /** Which side is the corp-lean 'A' option (always 'left' here — the scored option). */
  corpSide: 'left';
  /** Stable id for keying answers in the store. */
  id: string;
  /** Full left-anchor prose (corp / 'A' side). */
  leftAnchor: string;
  /** Terse left slider end-label. */
  leftLabel: string;
  /** The question prompt. */
  q: string;
  /** Full right-anchor prose (invest / 'B' side). */
  rightAnchor: string;
  /** Terse right slider end-label. */
  rightLabel: string;
}

export const M2_QUESTIONS: readonly M2Question[] = [
  {
    id: 'q1',
    q: "A company's revenue grew 20% last year, but net income fell 30%. What's your first instinct?",
    leftAnchor: '"Something broke on the cost side. I want to understand what\'s driving margin compression."',
    rightAnchor: '"Revenue growth is strong. I want to know if the market is underpricing the growth story."',
    leftLabel: 'Find the broken cost',
    rightLabel: 'Weigh the growth story',
    corpSide: 'left',
  },
  {
    id: 'q2',
    q: "You have $10,000 to put to work. What's your first step?",
    leftAnchor:
      '"I\'d figure out where that capital generates the highest internal return. Is it hiring, tooling, or paying down something expensive?"',
    rightAnchor: '"I\'d screen for companies trading below intrinsic value and build an investment thesis."',
    leftLabel: 'Build a business with it',
    rightLabel: 'Invest in one instead',
    corpSide: 'left',
  },
  {
    id: 'q3',
    q: "You're handed 5 years of financials for a company. What do you look at first?",
    leftAnchor:
      'Operating expenses and headcount. Are costs scaling in line with revenue, or are they getting out of control?',
    rightAnchor:
      'Revenue trajectory and gross margin. Is this a growing, high-quality business worth paying a premium for?',
    leftLabel: 'Are costs under control?',
    rightLabel: 'Is this a quality growing business?',
    corpSide: 'left',
  },
  {
    id: 'q4',
    q: 'What excites you more?',
    leftAnchor: 'Helping a CEO understand why the business underperformed and what to change',
    rightAnchor: "Calling a stock's upside before the market has priced it in",
    leftLabel: 'Help a CEO decide',
    rightLabel: "Call the stock's upside",
    corpSide: 'left',
  },
];

/** Slider value (0–6) → answer. Center (3) is 'gen' (neither A nor B). */
export function sliderToAnswer(value: number): M2Answer {
  if (value <= 2) {
    return 'A';
  }
  if (value >= 4) {
    return 'B';
  }
  return 'gen';
}

/** Answer → canonical slider stop (A→1, B→5, gen→3). */
export function answerToSlider(answer: M2Answer): number {
  if (answer === 'A') {
    return 1;
  }
  if (answer === 'B') {
    return 5;
  }
  return 3;
}
